import { ReactNode, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notFound } from '@tanstack/react-router';
import { proxy } from 'valtio';

import { useUserSocket } from '@features/shared/api';

import { updateTournamentCardQuery, useTournamentCardQuery } from '../../api/hooks';
import { TournamentContext } from './TournamentContext';
import { TournamentStore } from './TournamentStore';

export function TournamentProvider({
  clubId,
  tournamentId,
  children,
}: {
  clubId: string;
  tournamentId: string;
  children: ReactNode;
}) {
  // Загрузка турнира
  const query = useTournamentCardQuery(clubId, tournamentId);
  if (
    query.failureReason &&
    'httpCode' in query.failureReason &&
    query.failureReason.httpCode === 404
  ) {
    throw notFound();
  }

  // Отслеживание изменений турнира
  const ql = useQueryClient();
  useUserSocket((e) => {
    switch (e.eventType) {
      case 'TournamentStartsSoon':
      case 'TournamentFinished':
        if (e.tournamentId === tournamentId) {
          updateTournamentCardQuery(ql, clubId, tournamentId, e.tournament);
        }
        break;
      case 'RoundDrafted':
      case 'RoundStarted':
      case 'RoundFinished':
        if (e.tournamentId === tournamentId) {
          query.refetch();
        }
        break;
    }
  });

  // Стор
  const store = useMemo(
    () => proxy(new TournamentStore(clubId, tournamentId, query.refetch)),
    [clubId, tournamentId, query.refetch],
  );
  store.state.tournament = query.data;
  store.state.isLoading = query.isPending;
  store.state.isUpdating = !query.isLoading && query.isFetching;

  return <TournamentContext.Provider value={store}>{children}</TournamentContext.Provider>;
}
